"use client";

import { useRef, type ReactNode } from "react";
import { css, cx } from "styled-system/css";
import { gsap, useGSAP } from "@/lib/gsap-setup";

type Variant = "subtle" | "strong";

/**
 * Sección con fondo estilo panel (grilla + glow) y capas que se mueven con el scroll (parallax).
 */
export function LandingParallaxSection({
  variant,
  className,
  id,
  children,
}: {
  variant: Variant;
  className?: string;
  id?: string;
  children: ReactNode;
}) {
  const sectionRef = useRef<HTMLElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const tr = sectionRef.current;
      const grid = gridRef.current;
      const glow = glowRef.current;
      if (!tr || !grid || !glow) return;

      const gridY = variant === "subtle" ? 56 : 120;
      const glowY = variant === "subtle" ? -40 : -100;

      const stGrid = gsap.to(grid, {
        y: gridY,
        ease: "none",
        scrollTrigger: {
          trigger: tr,
          start: "top bottom",
          end: "bottom top",
          scrub: 1.15,
        },
      });

      const stGlow = gsap.to(glow, {
        y: glowY,
        ease: "none",
        scrollTrigger: {
          trigger: tr,
          start: "top bottom",
          end: "bottom top",
          scrub: 0.85,
        },
      });

      return () => {
        stGrid.scrollTrigger?.kill();
        stGlow.scrollTrigger?.kill();
      };
    },
    { scope: sectionRef, dependencies: [variant] },
  );

  const isSubtle = variant === "subtle";

  const gridLayer = css({
    position: "absolute",
    inset: "-100px",
    background:
      "linear-gradient(rgba(79, 70, 229, 0.055) 1px, transparent 1px), linear-gradient(90deg, rgba(56, 189, 248, 0.06) 1px, transparent 1px)",
    backgroundSize: "28px 28px",
    opacity: isSubtle ? 0.55 : 0.88,
    pointerEvents: "none",
    willChange: "transform",
  });

  const glowLayer = css({
    position: "absolute",
    inset: "-80px",
    background: isSubtle
      ? "radial-gradient(720px 440px at 14% 8%, rgba(99, 102, 241, 0.18), transparent 55%), radial-gradient(540px 380px at 88% 72%, rgba(14, 165, 233, 0.12), transparent 52%)"
      : "radial-gradient(700px 420px at 18% 12%, rgba(99, 102, 241, 0.32), transparent 58%)",
    pointerEvents: "none",
    willChange: "transform",
  });

  const baseBg = isSubtle
    ? undefined
    : css({
        bg: "#0b1220",
      });

  return (
    <section
      ref={sectionRef}
      id={id}
      className={cx(
        css({
          position: "relative",
          overflow: "hidden",
        }),
        baseBg,
        className,
      )}
    >
      <div ref={gridRef} className={gridLayer} aria-hidden />
      <div ref={glowRef} className={glowLayer} aria-hidden />
      <div
        className={css({
          position: "relative",
          zIndex: 1,
        })}
      >
        {children}
      </div>
    </section>
  );
}
